import { createAsyncThunk, createSlice } from '@reduxjs/toolkit';
import {
  HNFinancialAccountActivityConnection,
  HnFindFinancialAccountActivitiesQueryVariables,
  HnFindFinancialAccountLedgerActivityQueryVariables,
  HNLedger,
  HNPageInfo,
} from 'API';
import { RootState } from 'app/store/rootReducer';
import { highnoteService } from 'app/services/highnote.service';

export interface WalletState {
  financialAccountLedgersActivity: HNLedger[] | null | undefined;
  automaticFundingPendingActivity:
    | {
        amount: {
          value: number;
          currencyCode: string;
        };
        date: string;
      }
    | null
    | undefined;
  pageInfo?: HNPageInfo;
  isActivitiesLoading: boolean;
  loadMore: boolean;
}

const initialState: WalletState = {
  financialAccountLedgersActivity: null,
  automaticFundingPendingActivity: null,
  pageInfo: undefined,
  isActivitiesLoading: false,
  loadMore: false,
};

export const getFinancialAccountLedgerActivity = createAsyncThunk(
  'wallet/getFinancialAccountLedgerActivity',
  async ({ id, activitiesAfterCursor = undefined }: HnFindFinancialAccountLedgerActivityQueryVariables) => {
    return highnoteService.hnFindFinancialAccountLedgerActivity({
      id,
      firstActivities: 20,
      activitiesAfterCursor,
    });
  }
);

export const getFinancialAccountActivities = createAsyncThunk(
  'wallet/getFinancialAccountActivities',
  async (
    { id, activitiesAfterCursor = undefined }: HnFindFinancialAccountActivitiesQueryVariables,
    { rejectWithValue }
  ) => {
    // Validate ID before making GraphQL call
    if (!id || id.trim() === '') {
      console.error('[WALLET][getFinancialAccountActivities] Invalid ID provided:', { id });
      return rejectWithValue('Invalid financial account ID provided');
    }

    return highnoteService.hnFindFinancialAccountActivities({
      id,
      firstActivities: 20,
      activitiesAfterCursor,
      filterBy: {
        isComplete: {
          equals: false,
        },
      },
    });
  }
);

const walletSlice = createSlice({
  name: 'wallet',
  initialState,
  reducers: {
    resetWalletSlice: () => initialState,

    setLoadMore: (state) => {
      state.loadMore = true;
    },
  },

  extraReducers: (builder) => {
    builder
      .addCase(getFinancialAccountLedgerActivity.pending, (state) => {
        state.isActivitiesLoading = true;
      })
      .addCase(getFinancialAccountLedgerActivity.fulfilled, (state, action) => {
        const ledgerActivity = action.payload;
        if (ledgerActivity) {
          const ledgers = (ledgerActivity.ledgers || []).filter(Boolean) as HNLedger[];
          if (state.loadMore === true) {
            state.financialAccountLedgersActivity = [
              ...(state.financialAccountLedgersActivity || []),
              ...ledgers,
            ];
            state.loadMore = false;
          } else {
            state.financialAccountLedgersActivity = ledgers;
          }
          state.pageInfo = ledgers[0]?.ledgerEntries?.pageInfo ?? undefined;
        }
        state.isActivitiesLoading = false;
      });
    builder.addCase(getFinancialAccountActivities.fulfilled, (state, action) => {
      const activities = action.payload?.financialAccountActivities as HNFinancialAccountActivityConnection;

      activities.edges?.find(({ node }) => {
        if (
          node?.source?.__typename === 'HNIntegratorInitiatedFundsDepositACHTransfer' &&
          node?.source?.descriptor?.companyEntryDescription === 'Automatic'
        ) {
          state.automaticFundingPendingActivity = {
            amount: {
              value: node.pendingAmount?.value || 0,
              currencyCode: node.pendingAmount?.currencyCode || 'USD',
            },
            date: node.source.settlementDate || '',
          };
          return true;
        }
        return false;
      });
    });
  },
});

export const { resetWalletSlice, setLoadMore } = walletSlice.actions;

export const selectWalletSlice = (state: RootState): WalletState => state?.wallet ?? initialState;

export default walletSlice.reducer;
